import Workspace from 'iam/models/Workspace'
import ModalViewModel from 'infra/ModalViewModel'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import OCRScopeWorkStore from 'wunder/OCRScopeWorkStore'
import { DEFAULT, match } from 'utils/pattern-match-util'
import OCRScopeWorkWorkspaceSearchViewModel from '../../components/SidePanel/OCRScopeWorkWorkspaceSearchViewModel'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import Member from 'iam/models/Member'
import { Observable, switchMap } from 'rxjs'
import { required, validationContext } from 'validx'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { PublishedLegend } from 'legends/PublishedLegend'
import { CursorPageQuery, CursorPaged, Id } from 'types/definitions'
import { SUPPORTED_ENGINE_VERSION } from 'legends/API'
import { TemplateDTO } from 'legends/dtos/legend'
import MemberStore from 'iam/MemberStore'
import { CancellationToken } from 'infra/cancellation'
import TeamMember from 'iam/models/TeamMember'
import {
  FindTeamMembers,
  FindUserTeamMembersByUserPublicId,
} from 'iam/TeamMemberStore'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { ManualInputsAnswers } from 'wunder/dtos/ocr-scope-work'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { shareLatest } from 'utils/rx-util'
import { UserDataType } from 'iam/models/UserDataType'
import { QuestionDTO, SelectOptionDTO } from '../../../../dtos/questions'
import Team from 'iam/models/Team'
import { GetCurrentMember } from 'iam/SessionStore'

/**
 * Supported OCR file types.
 */
export const ACCEPTED_OCR_FILE_TYPES =
  '.jpeg, .jpg, .png, .bmp, .webp, .pdf, .tiff, .tif'

const ALLOWED_OCR_CLIENT_USER_ADMIN_TYPES = [
  UserDataType.User,
  UserDataType.Admin,
]

const ALLOWED_OCR_CLIENT_USER_TYPES = [UserDataType.User]

export enum CreateOCRScopeJobsViewModelStep {
  ManageDocuments,
  ScopeNewJobs,
}

export default class CreateOCRScopeJobsViewModel extends ModalViewModel {
  workspaceSelectorViewModel = new OCRScopeWorkWorkspaceSearchViewModel(
    this.getCurrentUserWorkspaceMemberships,
  )

  championClientSearchText = new SearchTextViewModel()

  supportingClientSearchText = new SearchTextViewModel()

  @observable
  createOCRScopeJobsViewModelStep =
    CreateOCRScopeJobsViewModelStep.ManageDocuments

  @observable
  dragging = false

  @observable
  selectedFiles: File[] = []

  @observable
  scopeJobMode: 'collectively' | 'individually' = 'collectively'

  @observable
  workspace?: Workspace | undefined = undefined

  @observable
  selectedFile?: File | undefined = undefined

  @observable
  legend?: PublishedLegend | undefined = undefined

  @observable
  legendSearchText = ''

  @observable
  templates: TemplateDTO[] | undefined = undefined

  @observable
  selectedTemplate?: TemplateDTO | undefined = undefined

  @observable
  championClient?: Member | undefined = undefined

  @observable
  supportingClient?: Member | undefined = undefined

  @observable
  clientTeamMember?: TeamMember | undefined = undefined

  @observable
  clientTeamSearchText = ''

  @observable
  championClientTeamMembershipsSearchText = ''

  @observable
  championClientTeamMembersSearchText = ''

  @observable
  supportingTeamMembersSearchText = ''

  @observable
  manualQuestions?: QuestionDTO[] | undefined = undefined

  @observable
  answers?: ManualInputsAnswers[] | undefined = undefined

  @observable
  selectAnswers: ManualInputsAnswers[] = []

  @observable
  isFetchingManualQuestions = false

  @observable
  saveAnswers = true

  /**
   * Whether the current user is only able to scope for themselves or team members.
   */
  @observable
  isNonAdminUser = false

  @observable
  selectedNonAdminUserTeam?: Team | undefined = undefined

  constructor(
    private ocrScopeWorkStore: OCRScopeWorkStore,
    private memberStore: MemberStore,
    private publishedLegendStore: PublishedLegendStore,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private currentMember$: Observable<Member>,
    private getCurrentMember: GetCurrentMember,
    private findTeamMembersByUserPublicId: FindUserTeamMembersByUserPublicId,
    private findTeamMembersByTeamId: FindTeamMembers,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)

    this.currentMember$.subscribe((member) => {
      this.setWorkspace(member.workspace)
      this.tryAutoSelectTeamForUser(member)

      // Only subscribe to these if the user has the ADMIN_USER_LIST permission.
      if (!this.isNonAdminUser) {
        this.championClientQuery$.pipe(this.whileShowing()).subscribe()
        this.supportingClientQuery$.pipe(this.whileShowing()).subscribe()
      }
    })

    this.showing$.subscribe(() => {
      this.tryAutoSelectLegendAndTemplate()

      if (this.isNonAdminUser) {
        this.setChampionClient(this.getCurrentMember())
      }
    })
    this.close$.subscribe(this.reset)
  }

  @computed
  get userTypesSearchFilter() {
    if (this.getCurrentMember().hasPermission('ADMIN_USER_LIST')) {
      return ALLOWED_OCR_CLIENT_USER_ADMIN_TYPES
    }

    return ALLOWED_OCR_CLIENT_USER_TYPES
  }

  @computed
  get selectedFilesCount() {
    return this.selectedFiles.length
  }

  @computed
  get disabled() {
    return match(this.scopeJobMode, {
      collectively: () =>
        !this.workspace ||
        !this.legend ||
        !this.selectedTemplate ||
        !this.championClient,
      individually: () =>
        !this.selectedFile ||
        !this.workspace ||
        !this.legend ||
        !this.selectedTemplate ||
        !this.championClient,
      [DEFAULT]: () => false,
    })
  }

  @computed
  get isOnlyOneFileSelected() {
    return this.selectedFilesCount === 1
  }

  @computed
  get workspaces() {
    return this.workspaceSelectorViewModel
      .workspacesWhereUserCanViewScopeWorkJobs
  }

  @computed
  get legends() {
    return this.legendScrollProvider.items
  }

  @computed
  get clientTeamMemberships() {
    return this.championClientTeamMembershipsScrollProvider.items
  }

  @computed
  get filteredLegends() {
    const lowerText = this.legendSearchText.toLowerCase()
    const legends = this.legends
    return legends.filter(
      (l) => !l.inactive && l.name.toLowerCase().includes(lowerText),
    )
  }

  @computed
  get canSelectLegend() {
    return this.workspace && this.legends.length !== 1
  }

  @computed
  get canSelectTemplate() {
    return (
      this.workspace &&
      this.legend &&
      this.templates &&
      this.templates.length !== 1
    )
  }

  @computed
  get canSelectClientTeam() {
    return this.workspace && this.championClient && !this.isNonAdminUser
  }

  @computed
  get canSelectManualQuestions() {
    return (
      this.selectedTemplate &&
      this.manualQuestions &&
      this.manualQuestions.length > 0
    )
  }

  @computed
  get canSaveAnswers() {
    return (
      this.selectedTemplate &&
      this.manualQuestions &&
      this.manualQuestions.length > 0 &&
      this.scopeJobMode === 'individually'
    )
  }

  @computed
  get canSelectTeamAsNonAdminUser() {
    return (
      this.workspace &&
      this.championClient &&
      this.clientTeamMemberships.length > 1
    )
  }

  @computed
  get canSelectSupportingClientAsNonAdminUser() {
    return (
      this.workspace && this.championClient && this.selectedNonAdminUserTeam
    )
  }

  @computed
  get canSelectSupportingClientAsAdminUser() {
    return this.workspace && this.championClient
  }

  @action.bound
  setLegendSearchText(text: string) {
    this.legendSearchText = text
    if (this.legendSearchText !== '') {
      this.legendScrollProvider.more()
    }
  }

  @action.bound
  setDragging() {
    this.dragging = true
  }

  @action.bound
  stopDragging() {
    this.dragging = false
  }

  @action.bound
  addFiles(files: FileList) {
    // Check if any of the files are unsupported.
    const unsupportedFileTypes: string[] = []
    const userFiles = Array.from(files)
    userFiles.forEach((file) => {
      const fileType = `.${file.type.split('/').pop()}`
      if (!ACCEPTED_OCR_FILE_TYPES.includes(fileType)) {
        unsupportedFileTypes.push(fileType)
      }
    })

    if (unsupportedFileTypes.length > 0) {
      this.newAlert()
        .show()
        .withState(AlertState.Warning)
        .saying(
          `Unsupported file ${
            unsupportedFileTypes.length === 1 ? 'type' : 'types'
          } uploaded: `,
          `${unsupportedFileTypes.join(', ')}`,
        )
        .autoDismiss()

      return
    }

    this.selectedFiles.push(...userFiles)
  }

  @action.bound
  removeFile(fileToRemove: File) {
    this.selectedFiles = this.selectedFiles.filter(
      (file) => file !== fileToRemove,
    )
  }

  @action.bound
  goToManageDocuments() {
    this.createOCRScopeJobsViewModelStep =
      CreateOCRScopeJobsViewModelStep.ManageDocuments
  }

  @action.bound
  goToScopeNewJobs() {
    if (this.isOnlyOneFileSelected) {
      this.scopeJobMode = 'individually'
      this.selectedFile = this.selectedFiles[0]
    }

    this.scopeJobMode = 'collectively'
    this.createOCRScopeJobsViewModelStep =
      CreateOCRScopeJobsViewModelStep.ScopeNewJobs
  }

  @action.bound
  setIsNonAdminUser(isNonAdminUser: boolean) {
    this.isNonAdminUser = isNonAdminUser
  }

  @action.bound
  setSelectedNonAdminUserTeam(team?: Team | undefined) {
    this.selectedNonAdminUserTeam = team

    this.supportingClient = undefined
    this.clientTeamMember = undefined
    this.championClientTeamMembersScrollProvider.reset(false)
  }

  @action.bound
  setScopeJobMode(mode: 'collectively' | 'individually') {
    this.scopeJobMode = mode
  }

  @action.bound
  setWorkspace(workspace?: Workspace | undefined) {
    this.workspace = workspace
    this.legend = undefined
    this.selectedTemplate = undefined
    this.templates = undefined
    this.championClient = undefined
    this.supportingClient = undefined
    this.clientTeamMember = undefined

    if (this.workspace) {
      if (this.isNonAdminUser) {
        this.selectedNonAdminUserTeam = undefined
        this.championClientTeamMembershipsScrollProvider.reset(false)
        this.championClientTeamMembersScrollProvider.reset(false)

        this.tryAutoSelectTeamForUser(this.getCurrentMember(this.workspace.id))
      }

      if (!this.isNonAdminUser) {
        this.championClientScrollProvider.reset(false)
        this.supportingClientScrollProvider.reset(false)
      }

      this.clientTeamMembersScrollProvider.reset(false)
      this.tryAutoSelectLegendAndTemplate()
    }
  }

  @action.bound
  setSelectedFile(file?: File | undefined) {
    this.selectedFile = file
  }

  @action.bound
  setLegend(legend?: PublishedLegend | undefined) {
    this.legend = legend

    this.selectedTemplate = undefined
    this.templates = undefined

    if (this.legend) {
      this.publishedLegendStore
        .fetchLegendVersion(this.legend.versionId, SUPPORTED_ENGINE_VERSION)
        .then((legendVersion) => this.setTemplates(legendVersion.templates))
        .finally(() => {
          if (this.templates && this.templates.length === 1) {
            this.setSelectedTemplate(this.templates[0])
          }
        })
    }
  }

  @action.bound
  setTemplates(templates: TemplateDTO[] | undefined) {
    this.templates = templates
  }

  @action.bound
  setSelectedTemplate(template?: TemplateDTO | undefined) {
    this.selectedTemplate = template

    this.manualQuestions = undefined
    this.answers = undefined
    this.selectAnswers = []

    if (this.selectedTemplate) {
      this.setIsFetchingManualQuestions(true)
      this.ocrScopeWorkStore
        .manualInputs({
          workspace_id: Id.assert(this.workspace?.id),
          legend_id: Id.assert(this.legend?.id),
          legend_version: Id.assert(this.legend?.version.toString()),
          template_id: this.selectedTemplate?.id,
        })
        .then((questions) => {
          this.setManualQuestions(questions)
          this.setAnswers(
            questions.map((x) => ({
              id: x.id ?? '',
              answer: x.answer,
            })),
          )
          this.setIsFetchingManualQuestions(false)
        })
    }
  }

  @action.bound
  setManualQuestions(questions?: QuestionDTO[] | undefined) {
    this.manualQuestions = questions
  }

  @action.bound
  setAnswers(answers?: ManualInputsAnswers[] | undefined) {
    this.answers = answers
  }

  @action.bound
  updateAnswer(answer: unknown, id?: string) {
    if (!id) {
      return
    }

    const foundAnswer = this.answers?.find((i) => i.id === id)

    if (foundAnswer) {
      foundAnswer.answer = answer
    }
  }

  @action.bound
  updatePotentialMultiSelectAnswer(
    answer: unknown,
    id?: string,
    multiSelect?: boolean,
    multiSelectValue?: boolean | SelectOptionDTO | SelectOptionDTO[],
  ) {
    if (!id) {
      return
    }

    const foundAnswer = this.answers?.find((i) => i.id === id)

    if (foundAnswer && multiSelect && Array.isArray(multiSelectValue)) {
      foundAnswer.answer = multiSelectValue

      const foundSelectAnswer = this.selectAnswers.find((i) => i.id === id)
      if (foundSelectAnswer) {
        foundSelectAnswer.answer = multiSelectValue.map((x) => x.id)
      } else {
        this.selectAnswers.push({
          id,
          answer: multiSelectValue.map((x) => x.id),
        })
      }

      return
    }

    if (foundAnswer && !Array.isArray(answer)) {
      const selectAnswer = answer as SelectOptionDTO
      foundAnswer.answer = selectAnswer
      const foundSelectAnswer = this.selectAnswers.find((i) => i.id === id)
      if (foundSelectAnswer) {
        foundSelectAnswer.answer = selectAnswer?.id || ''
      } else {
        this.selectAnswers.push({
          id,
          answer: selectAnswer.id,
        })
      }
    }
  }

  @action.bound
  setChampionClient(client?: Member | undefined) {
    // Disallow deselecting the champion client if the user is a basic user.
    if (!client && this.isNonAdminUser) {
      return
    }

    this.championClient = client

    this.supportingClient = undefined
    this.clientTeamMember = undefined

    if (this.isNonAdminUser) {
      this.championClientTeamMembersScrollProvider.reset(false)
    }

    if (!this.isNonAdminUser) {
      this.supportingClientScrollProvider.reset(false)
    }

    this.clientTeamMembersScrollProvider.reset(false)
  }

  @action.bound
  setSupportingClient(client?: Member | undefined) {
    this.supportingClient = client
  }

  @action.bound
  setClientTeamMember(team?: TeamMember | undefined) {
    this.clientTeamMember = team
  }

  @action.bound
  toggleSaveAnswers() {
    this.saveAnswers = !this.saveAnswers
  }

  @computed
  get filteredClientTeam() {
    const lowerText = this.clientTeamSearchText.toLowerCase()
    const clientTeamItems = this.clientTeamMembersScrollProvider.items
    const clientTeam = this.selectedNonAdminUserTeam
      ? clientTeamItems.filter(
          (tm) => tm.team.id === this.selectedNonAdminUserTeam?.id,
        )
      : clientTeamItems
    return clientTeam.filter((l) =>
      l.team.name.toLowerCase().includes(lowerText),
    )
  }

  @computed
  get filteredChampionClientTeamMemberships() {
    const lowerText = this.championClientTeamMembershipsSearchText.toLowerCase()
    const clientTeam = this.championClientTeamMembershipsScrollProvider.items
    return clientTeam.filter((l) => l.name.toLowerCase().includes(lowerText))
  }

  @computed
  get filteredChampionClientTeamMembers() {
    const lowerText = this.championClientTeamMembersSearchText.toLowerCase()
    const teamMembers = this.championClientTeamMembersScrollProvider.items
    return teamMembers.filter((l) => {
      return (
        l.displayName.toLowerCase().includes(lowerText) ||
        l.email.toLowerCase().includes(lowerText)
      )
    })
  }

  @computed
  get filteredSupportingTeamMembers() {
    const lowerText = this.supportingTeamMembersSearchText.toLowerCase()
    const teamMembers =
      this.championClientTeamMembersScrollProvider.items.filter(
        (m) => m.user.publicId !== this.championClient?.user.publicId,
      )
    return teamMembers.filter((l) => {
      return (
        l.displayName.toLowerCase().includes(lowerText) ||
        l.email.toLowerCase().includes(lowerText)
      )
    })
  }

  @action.bound
  setClientTeamSearchText(text: string) {
    this.clientTeamSearchText = text
    if (this.clientTeamSearchText !== '') {
      this.clientTeamMembersScrollProvider.more()
    }
  }

  @action.bound
  setChampionClientTeamMembershipsSearchText(text: string) {
    this.championClientTeamMembershipsSearchText = text
    if (this.championClientTeamMembershipsSearchText !== '') {
      this.championClientTeamMembershipsScrollProvider.more()
    }
  }

  @action.bound
  setChampionClientTeamMembersSearchText(text: string) {
    this.championClientTeamMembersSearchText = text
    if (this.championClientTeamMembersSearchText !== '') {
      this.championClientTeamMembersScrollProvider.more()
    }
  }

  @action.bound
  setSupportingTeamMembersSearchText(text: string) {
    this.supportingTeamMembersSearchText = text
    if (this.supportingTeamMembersSearchText !== '') {
      this.championClientTeamMembersScrollProvider.more()
    }
  }

  @action.bound
  setIsFetchingManualQuestions(isFetching: boolean) {
    this.isFetchingManualQuestions = isFetching
  }

  onChangeScopeJobMode(mode: 'collectively' | 'individually') {
    if (mode === 'individually' && this.selectedFilesCount > 0) {
      this.selectedFile = this.selectedFiles[0]
    }

    this.setScopeJobMode(mode)
  }

  /**
   * Validation context.
   */
  validation = validationContext(this, {
    workspace: [required('Workspace is required.')],
    legend: [required('Legend is required.')],
    selectedTemplate: [required('Template is required.')],
    championClient: [required('Champion client is required.')],
  })

  /**
   * Validates the view model.
   */
  validate() {
    this.validation.reset()

    if (!this.selectedFile && this.scopeJobMode === 'individually') {
      this.validation.addErrors({
        selectedFile: ['A file is required.'],
      })

      return this.validation.validate().isValid
    }

    return this.validation.validate().isValid
  }

  championClientQuery$ = this.championClientSearchText.debouncedValue$.pipe(
    switchMap(() => this.championClientScrollProvider.fetch()),
    shareLatest(),
  )

  supportingClientQuery$ = this.supportingClientSearchText.debouncedValue$.pipe(
    switchMap(() => this.supportingClientScrollProvider.fetch()),
    shareLatest(),
  )

  @action
  open() {
    this.show()
  }

  @action.bound
  reset() {
    this.championClientSearchText.clear()
    this.supportingClientSearchText.clear()
    this.createOCRScopeJobsViewModelStep =
      CreateOCRScopeJobsViewModelStep.ManageDocuments
    this.selectedFiles = []
    this.scopeJobMode = 'collectively'
    this.selectedFile = undefined
    this.legend = undefined
    this.legendSearchText = ''
    this.templates = undefined
    this.selectedTemplate = undefined
    this.championClient = undefined
    this.supportingClient = undefined
    this.clientTeamMember = undefined
    this.clientTeamSearchText = ''
    this.championClientTeamMembershipsSearchText = ''
    this.championClientTeamMembersSearchText = ''
    this.supportingTeamMembersSearchText = ''
    this.manualQuestions = undefined
    this.answers = undefined
    this.selectAnswers = []
    this.isFetchingManualQuestions = false
    this.saveAnswers = true

    this.validation.reset()
    this.submit.reset()
  }

  /**
   * Tries to auto select the legend and template when a workspace is selected.
   * If there is only one legend, it will be selected.
   * If there is only one template, it will be selected.
   */
  @action.bound
  tryAutoSelectLegendAndTemplate() {
    if (!this.workspace) {
      return
    }

    this.legendScrollProvider.fetch().then(() => {
      if (this.legends.length === 1) {
        this.setLegend(this.legends[0])
      }
    })
  }

  /**
   * Tries to auto select the team that the user belongs to.
   * Only if the user does not have the ADMIN_USER_LIST permission.
   * If there is only one team, it will be selected.
   * If there is multiple teams, the user will have to select one.
   */
  @action.bound
  tryAutoSelectTeamForUser(currentMember: Member) {
    if (
      currentMember.hasPermission('ADMIN_USER_LIST') ||
      currentMember.hasPermission('ADMIN_CLIENT_USER_LIST')
    ) {
      return
    }

    this.setIsNonAdminUser(true)
    // Set the champ client as the current user.
    this.setChampionClient(currentMember)

    this.championClientTeamMembershipsScrollProvider.fetch().then(() => {
      if (this.clientTeamMemberships.length === 1) {
        this.setSelectedNonAdminUserTeam(this.clientTeamMemberships[0])
      }
    })
  }

  legendScrollProvider = new InfiniteScrollProvider<PublishedLegend>({
    limit: 30,
    startFetchTaskAsPending: true,
    fetchItems: (paging) =>
      this.publishedLegendStore.findLegends({
        workspace_id: Id.assert(this.workspace?.id),
        after: paging.after ?? '',
        limit: paging.limit ?? 30,
      }),
  })

  championClientScrollProvider = new InfiniteScrollProvider<Member>({
    limit: 20,
    startFetchTaskAsPending: true,
    fetchItems: (paging) =>
      this.searchChampionClient(this.championClientSearchText.value, paging),
  })

  supportingClientScrollProvider = new InfiniteScrollProvider<Member>({
    limit: 20,
    startFetchTaskAsPending: true,
    fetchItems: (paging) =>
      this.searchSupportingClient(
        this.supportingClientSearchText.value,
        paging,
      ),
  })

  clientTeamMembersScrollProvider = new InfiniteScrollProvider<TeamMember>({
    limit: 20,
    startFetchTaskAsPending: true,
    fetchItems: (paging) => this.fetchClientTeamMembers(paging),
  })

  championClientTeamMembershipsScrollProvider =
    new InfiniteScrollProvider<Team>({
      limit: 20,
      startFetchTaskAsPending: true,
      fetchItems: (paging) => this.fetchChampionClientTeamMemberships(paging),
    })

  championClientTeamMembersScrollProvider = new InfiniteScrollProvider<Member>({
    limit: 20,
    startFetchTaskAsPending: true,
    fetchItems: (paging) => this.fetchChampionClientTeamMembers(paging),
  })

  searchChampionClient = async (
    searchText: string,
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<Member>> => {
    const paged = await this.memberStore.search(
      {
        after: params.after,
        limit: params.limit,
        workspace_id: Id.assert(this.workspace?.id),
        search_text: searchText,
        user_types: this.userTypesSearchFilter,
      },
      cancellationToken,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate = m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }

  searchSupportingClient = async (
    searchText: string,
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<Member>> => {
    const paged = await this.memberStore.search(
      {
        after: params.after,
        limit: params.limit,
        workspace_id: Id.assert(this.workspace?.id),
        search_text: searchText,
        user_types: this.userTypesSearchFilter,
      },
      cancellationToken,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          m.user.id === 'SYSTEM_ROBOT' || m === this.championClient
        return !invalidCandidate
      }),
    )
  }

  fetchClientTeamMembers = async (
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<TeamMember>> => {
    return CursorPaged.fromArray(
      await this.findTeamMembersByUserPublicId(
        {
          after: params.after,
          limit: params.limit,
          workspace_id: Id.assert(this.workspace?.id),
          user_public_id: Id.assert(this.championClient?.user.publicId),
          include_team: true,
        },
        cancellationToken,
      ),
    )
  }

  fetchChampionClientTeamMemberships = async (
    params: CursorPageQuery,
    cancellationToken = CancellationToken.default,
  ): Promise<CursorPaged<Team>> => {
    return CursorPaged.fromArray(
      await this.findTeamMembersByUserPublicId(
        {
          after: params.after,
          limit: params.limit,
          workspace_id: Id.assert(this.workspace?.id),
          user_public_id: Id.assert(this.championClient?.user.publicId),
          include_team: true,
        },
        cancellationToken,
      ).then((data) => data.map((tm) => tm.team)),
    )
  }

  fetchChampionClientTeamMembers = async (
    params: CursorPageQuery,
  ): Promise<CursorPaged<Member>> => {
    if (!this.selectedNonAdminUserTeam) {
      const championClient = this.championClient ?? this.getCurrentMember()
      return CursorPaged.fromArray([championClient])
    }

    const paged = await this.findTeamMembersByTeamId({
      after: params.after,
      limit: params.limit,
      team_id: Id.assert(this.selectedNonAdminUserTeam.id),
      inactive: false,
      include_team: true,
    })

    return CursorPaged.map(paged, (data) => data.map((tm) => tm.member))
  }

  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const selectedIds = new Set(this.selectAnswers.map((item) => item.id))

    const filteredAnswers = this.answers?.filter(
      ({ id }) => !selectedIds.has(id),
    )

    const data: { [id: string]: any } = {}
    filteredAnswers?.forEach(({ id, answer }) => {
      data[id] = answer
    })

    this.selectAnswers?.forEach(({ id, answer }) => {
      data[id] = answer
    })

    if (Object.values(data).length !== this.manualQuestions?.length) {
      this.newAlert()
        .show()
        .withState(AlertState.Warning)
        .saying('Please answer all required questions')
        .autoDismiss()

      return
    }

    const questionsWithEmptyAnswers = Object.values(data).filter(
      (value) => value === '' || (Array.isArray(value) && value.length === 0),
    )
    const isAnyAnswerEmpty = questionsWithEmptyAnswers.length > 0

    if (isAnyAnswerEmpty) {
      this.newAlert()
        .show()
        .withState(AlertState.Warning)
        .saying(
          'Please answer all questions',
          `${questionsWithEmptyAnswers.length} unanswered questions remaining.`,
        )
        .autoDismiss()

      return
    }

    if (this.scopeJobMode === 'individually') {
      await this.ocrScopeWorkStore.scopeWork({
        workspace_id: Id.assert(this.workspace?.id),
        user_id: Id.assert(this.championClient?.user.publicId),
        supporting_client_user_public_id: this.supportingClient?.user.publicId,
        team_id: this.isNonAdminUser
          ? this.selectedNonAdminUserTeam?.id
          : this.clientTeamMember?.team.id,
        legend_id: Id.assert(this.legend?.id),
        legend_version: this.legend!.version,
        template_id: Id.assert(this.selectedTemplate?.id),
        data,
        files: [this.selectedFile!],
      })

      // Remove that file from the selected files, since its already being scoped.
      this.removeFile(this.selectedFile!)

      if (this.selectedFilesCount === 0) {
        this.newAlert()
          .show()
          .withState(AlertState.Success)
          .saying('Success!', `The job is currently being scoped.`)
          .autoDismiss()
        this.close()

        return
      }

      // Select the next file to be scoped.
      this.setSelectedFile(this.selectedFiles[0])

      this.newAlert()
        .show()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          'Job is being scoped, please continue with the next file.',
        )
        .autoDismiss()

      // If the user doesn't want to save the answers, reset them.
      if (!this.saveAnswers) {
        this.setSelectedTemplate(this.selectedTemplate)
      }

      return
    }

    await this.ocrScopeWorkStore.scopeWork({
      workspace_id: Id.assert(this.workspace?.id),
      user_id: Id.assert(this.championClient?.user.publicId),
      supporting_client_user_public_id: this.supportingClient?.user.publicId,
      team_id: this.isNonAdminUser
        ? this.selectedNonAdminUserTeam?.id
        : this.clientTeamMember?.team.id,
      legend_id: Id.assert(this.legend?.id),
      legend_version: this.legend!.version,
      template_id: Id.assert(this.selectedTemplate?.id),
      data,
      files: this.selectedFiles,
    })

    this.newAlert()
      .show()
      .withState(AlertState.Success)
      .saying(
        'Success!',
        `The ${
          this.selectedFilesCount === 1 ? 'job is' : 'jobs are'
        } currently being scoped.`,
      )
      .autoDismiss()

    this.close()
  })
}
